<template>
  <div class="base-preview-root">
    <div v-if="url" class="base-preview-toolbar">
      <div v-if="previewMode === 'image'" class="base-preview-toolbar__zoom">
        <Button
          size="small"
          ghost
          icon="md-remove"
          :disabled="imageScale <= minImageScale"
          @click="zoomOut"
        />
        <span class="base-preview-toolbar__scale">{{ scalePercent }}%</span>
        <Button
          size="small"
          ghost
          icon="md-add"
          :disabled="imageScale >= maxImageScale"
          @click="zoomIn"
        />
        <Button size="small" ghost @click="resetImageView">适应</Button>
      </div>
      <div v-else class="base-preview-toolbar__spacer" />
      <Button
        size="small"
        icon="md-download"
        @click="handleDownload"
      >
        下载文件
      </Button>
    </div>
    <div
      v-if="previewMode === 'image'"
      ref="imageViewport"
      class="base-preview base-preview--image"
      @wheel.prevent="onImageWheel"
      @dblclick="resetImageView"
    >
      <div
        class="base-preview__image-wrap"
        :class="{ 'is-panning': panning }"
        :style="imageTransformStyle"
        @mousedown.prevent="onImagePanStart"
        @touchstart.prevent="onImageTouchStart"
      >
        <img
          :src="url"
          class="base-preview__image"
          alt="预览"
          draggable="false"
        />
      </div>
    </div>
    <div
      v-else
      class="base-preview"
      :class="[`base-preview--${previewMode}`, { 'base-preview--pdf': isPdf }]"
    >
      <object
        v-if="previewMode === 'iframe' && isPdf && useMobilePdfView"
        :data="pdfEmbedUrl"
        type="application/pdf"
        class="base-preview__pdf-object"
      />
      <iframe
        v-else-if="previewMode === 'iframe'"
        :src="iframeSrc"
        class="base-preview__iframe"
        frameborder="0"
        allowfullscreen
      />
      <div v-else class="base-preview__fallback">
        该文件格式 {{ fileExt || '未知' }} 无法预览，请点击上方下载按钮
      </div>
    </div>
  </div>
</template>

<script>
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico'];
const PDF_EXTS = ['pdf'];
const OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const MIN_IMAGE_SCALE = 0.25;
const MAX_IMAGE_SCALE = 5;
const IMAGE_SCALE_STEP = 0.25;

export default {
  props: {
    url: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      imageScale: 1,
      imageTranslateX: 0,
      imageTranslateY: 0,
      panning: false,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
      imageClickMoved: false,
      touchPanning: false,
      touchPinching: false,
      pinchStartDistance: 0,
      pinchStartScale: 1,
      minImageScale: MIN_IMAGE_SCALE,
      maxImageScale: MAX_IMAGE_SCALE,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    };
  },
  computed: {
    isPdf() {
      return PDF_EXTS.includes(this.fileExt);
    },
    useMobilePdfView() {
      return this.viewportWidth <= 768;
    },
    pdfEmbedUrl() {
      if (!this.url) return '';
      const base = String(this.url).split('#')[0];
      return `${base}#view=FitH&zoom=page-width`;
    },
    fileExt() {
      return this.getFileExt(this.url);
    },
    downloadName() {
      if (this.name) {
        return this.name;
      }
      if (this.url && this.$util && typeof this.$util.parseFileURL === 'function') {
        const parsed = this.$util.parseFileURL(this.url);
        if (parsed && parsed.name) {
          return parsed.name;
        }
      }
      return '文件';
    },
    previewMode() {
      if (!this.url) {
        return 'unsupported';
      }
      if (IMAGE_EXTS.includes(this.fileExt)) {
        return 'image';
      }
      if (PDF_EXTS.includes(this.fileExt) || OFFICE_EXTS.includes(this.fileExt)) {
        return 'iframe';
      }
      return 'unsupported';
    },
    scalePercent() {
      return Math.round(this.imageScale * 100);
    },
    imageTransformStyle() {
      return {
        transform: `translate(${this.imageTranslateX}px, ${this.imageTranslateY}px) scale(${this.imageScale})`,
      };
    },
    iframeSrc() {
      if (!this.url) {
        return '';
      }
      if (OFFICE_EXTS.includes(this.fileExt)) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(this.url)}`;
      }
      if (PDF_EXTS.includes(this.fileExt)) {
        const base = String(this.url).split('#')[0];
        return `${base}#toolbar=0&navpanes=0&view=FitH&zoom=page-width`;
      }
      return this.url;
    },
  },
  watch: {
    url() {
      this.resetImageView();
    },
  },
  mounted() {
    this.onViewportResize = () => {
      this.viewportWidth = window.innerWidth;
    };
    window.addEventListener('resize', this.onViewportResize, {passive: true});
  },
  beforeDestroy() {
    if (this.onViewportResize) {
      window.removeEventListener('resize', this.onViewportResize);
    }
    this.detachPanListeners();
    this.onImageTouchEnd();
  },
  methods: {
    getFileExt(url) {
      if (!url) {
        return '';
      }
      const path = String(url).split('?')[0].split('#')[0];
      const dot = path.lastIndexOf('.');
      if (dot === -1) {
        return '';
      }
      return path.substring(dot + 1).toLowerCase();
    },
    handleDownload() {
      if (!this.url) {
        return;
      }
      this.DownloadWithName(this.url, this.downloadName);
    },
    resetImageView() {
      this.imageScale = 1;
      this.imageTranslateX = 0;
      this.imageTranslateY = 0;
      this.panning = false;
      this.imageClickMoved = false;
    },
    clampImageScale(scale) {
      return Math.max(MIN_IMAGE_SCALE, Math.min(MAX_IMAGE_SCALE, scale));
    },
    setImageScale(scale) {
      this.imageScale = this.clampImageScale(scale);
    },
    zoomIn() {
      this.setImageScale(this.imageScale + IMAGE_SCALE_STEP);
    },
    zoomOut() {
      this.setImageScale(this.imageScale - IMAGE_SCALE_STEP);
    },
    onImageWheel(e) {
      const delta = e.deltaY > 0 ? -IMAGE_SCALE_STEP : IMAGE_SCALE_STEP;
      this.setImageScale(this.imageScale + delta);
    },
    onImagePanStart(e) {
      if (e.button != null && e.button !== 0) return;
      this.panning = true;
      this.imageClickMoved = false;
      this.panStartX = e.clientX;
      this.panStartY = e.clientY;
      this.panOriginX = this.imageTranslateX;
      this.panOriginY = this.imageTranslateY;
      document.addEventListener('mousemove', this.onImagePanMove);
      document.addEventListener('mouseup', this.onImagePanEnd);
    },
    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    },
    onImageTouchStart(e) {
      if (!e.touches || !e.touches.length) return;
      if (e.touches.length >= 2) {
        this.touchPinching = true;
        this.touchPanning = false;
        this.pinchStartDistance = this.getTouchDistance(e.touches);
        this.pinchStartScale = this.imageScale;
        document.addEventListener('touchmove', this.onImageTouchMove, {passive: false});
        document.addEventListener('touchend', this.onImageTouchEnd);
        document.addEventListener('touchcancel', this.onImageTouchEnd);
        return;
      }
      this.touchPanning = true;
      this.touchPinching = false;
      this.imageClickMoved = false;
      this.panStartX = e.touches[0].clientX;
      this.panStartY = e.touches[0].clientY;
      this.panOriginX = this.imageTranslateX;
      this.panOriginY = this.imageTranslateY;
      document.addEventListener('touchmove', this.onImageTouchMove, {passive: false});
      document.addEventListener('touchend', this.onImageTouchEnd);
      document.addEventListener('touchcancel', this.onImageTouchEnd);
    },
    onImageTouchMove(e) {
      if (this.touchPinching && e.touches && e.touches.length >= 2) {
        e.preventDefault();
        const distance = this.getTouchDistance(e.touches);
        if (!this.pinchStartDistance) return;
        const ratio = distance / this.pinchStartDistance;
        this.setImageScale(this.pinchStartScale * ratio);
        return;
      }
      if (!this.touchPanning || !e.touches || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - this.panStartX;
      const dy = touch.clientY - this.panStartY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.imageClickMoved = true;
      }
      this.imageTranslateX = this.panOriginX + dx;
      this.imageTranslateY = this.panOriginY + dy;
    },
    onImageTouchEnd() {
      this.touchPanning = false;
      this.touchPinching = false;
      this.pinchStartDistance = 0;
      document.removeEventListener('touchmove', this.onImageTouchMove);
      document.removeEventListener('touchend', this.onImageTouchEnd);
      document.removeEventListener('touchcancel', this.onImageTouchEnd);
    },
    onImagePanMove(e) {
      const dx = e.clientX - this.panStartX;
      const dy = e.clientY - this.panStartY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.imageClickMoved = true;
      }
      this.imageTranslateX = this.panOriginX + dx;
      this.imageTranslateY = this.panOriginY + dy;
    },
    onImagePanEnd() {
      this.panning = false;
      this.detachPanListeners();
    },
    detachPanListeners() {
      document.removeEventListener('mousemove', this.onImagePanMove);
      document.removeEventListener('mouseup', this.onImagePanEnd);
    },
  },
};
</script>

<style lang="less" scoped>
.base-preview-root {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 240px;
  box-sizing: border-box;
  overflow: hidden;
}

.base-preview-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border, #e8eaec);
  background: var(--bg2, #fff);
}

.base-preview-toolbar__zoom {
  display: flex;
  align-items: center;
  gap: 8px;
}

.base-preview-toolbar__scale {
  min-width: 48px;
  text-align: center;
  font-size: 12px;
  color: var(--subtext2, #808695);
}

.base-preview-toolbar__spacer {
  flex: 1;
}

.base-preview-toolbar__download {
  color: #5a5b5c;
  border-color: transparent;
  box-shadow: none;

  /deep/ .ivu-icon {
    color: #a8abb2;
  }

  &:hover,
  &:focus {
    color: #808695;
    background: rgba(0, 0, 0, 0.04);
    border-color: transparent;
  }

  &:hover /deep/ .ivu-icon,
  &:focus /deep/ .ivu-icon {
    color: #808695;
  }
}

.base-preview {
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: var(--bg2, #f5f7fa);
}

.base-preview--image {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 12px;
  user-select: none;
}

.base-preview__image-wrap {
  transition: transform 0.15s ease;
  will-change: transform;
  cursor: grab;
  touch-action: none;

  &.is-panning {
    transition: none;
    cursor: grabbing;
  }
}

.base-preview__image {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 180px);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  pointer-events: auto;
}

.base-preview--iframe {
  overflow: hidden;
  padding: 0;
  background: #525659;
}

.base-preview--pdf {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.base-preview__iframe,
.base-preview__pdf-object {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 100%;
  border: none;
  background: #fff;
  box-sizing: border-box;
}

.base-preview__pdf-object {
  object-fit: contain;
}

@media (max-width: 768px) {
  .base-preview--pdf {
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .base-preview__iframe,
  .base-preview__pdf-object {
    width: 100% !important;
    max-width: 100% !important;
  }

  .base-preview--image {
    padding: 8px;
  }

  .base-preview__image {
    max-height: calc(100dvh - 120px);
  }
}

.base-preview--unsupported {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.base-preview__fallback {
  color: var(--subtext2, #808695);
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}
</style>
